import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';
import './EmailCentral.css';

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return 'Jamais';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' })
    + ' ' + d.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
}

function formatDateCourte(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric' });
}

const BADGE_TYPE = {
  transactionnel: { label: 'Transactionnel', cls: 'badge--info' },
  campagne:       { label: 'Campagne',        cls: 'badge--warning' },
  notification:   { label: 'Notification',    cls: '' },
};

const BADGE_DECLENCHEUR = {
  automatique:           { label: 'Automatique',          cls: 'badge--success' },
  manuel:                { label: 'Manuel',               cls: 'badge--warning' },
  confirmation_requise:  { label: 'Confirmation requise', cls: 'badge--danger' },
};

const LABELS_LISTE = {
  clients:  'Clients événements publics',
  corpo:    'Clients corporatifs',
  attente:  "Liste d'attente",
  boutique: 'Acheteurs boutique WC',
  master:   'Master List',
  interne:  'Interne (LCU)',
};

const OPTIONS_LISTE = [
  { cle: 'master',   label: 'Master List' },
  { cle: 'clients',  label: 'Clients événements publics' },
  { cle: 'corpo',    label: 'Clients corporatifs' },
  { cle: 'attente',  label: "Liste d'attente" },
  { cle: 'boutique', label: 'Acheteurs boutique WC' },
  { cle: 'manuel',   label: 'Emails spécifiques' },
];

// Icônes par type de liste (noms Icon.jsx)
const ICONE_LISTE_MAP = {
  clients:  'ribs',
  corpo:    'entreprise',
  attente:  'heure',
  boutique: 'panier',
  master:   'etiquette',
  interne:  'verrou',
  manuel:   'modifier',
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function EmailSkeleton() {
  return (
    <div className="email-central rise-in">
      <div className="page-header">
        <div className="page-header-text">
          <div className="skeleton" style={{ height: 34, width: 280, marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 13, width: 320 }} />
        </div>
        <div className="skeleton" style={{ height: 34, width: 180, borderRadius: 'var(--radius-md)' }} />
      </div>
      <div className="email-tabs" style={{ marginBottom: 'var(--space-6)' }}>
        {[0,1,2].map(i => (
          <div key={i} className="skeleton" style={{ height: 38, width: 100, borderRadius: 'var(--radius-sm)' }} />
        ))}
      </div>
      <div className="campagnes-grid">
        {[0,1,2].map(i => (
          <div key={i} className="card" style={{ minHeight: 160, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', padding: 'var(--space-6)' }}>
            <div className="skeleton" style={{ height: 14, width: '70%' }} />
            <div className="skeleton" style={{ height: 11, width: '50%' }} />
            <div className="skeleton" style={{ height: 32, width: 90, marginTop: 'auto', borderRadius: 'var(--radius-md)' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────────

export default function EmailCentral() {
  const [onglet,     setOnglet]     = useState('campagnes');
  const [campagnes,  setCampagnes]  = useState([]);
  const [listes,     setListes]     = useState([]);
  const [logs,       setLogs]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [msg,        setMsg]        = useState(null);
  const [modalEnvoi, setModalEnvoi] = useState(null);
  const [syncing,    setSyncing]    = useState(false);
  const [filtreLog,  setFiltreLog]  = useState({ date_debut: '', date_fin: '' });

  // ── Chargement ─────────────────────────────────────────────────────────────

  const chargerCampagnes = useCallback(async () => {
    try {
      const data = await api.get('/emails/campagnes');
      setCampagnes(Array.isArray(data) ? data : []);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  const chargerListes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get('/emails/listes');
      setListes(Array.isArray(data) ? data : []);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  const chargerLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtreLog.date_debut) params.set('date_debut', filtreLog.date_debut);
      if (filtreLog.date_fin)   params.set('date_fin',   filtreLog.date_fin);
      const data = await api.get(`/emails/log?${params}`);
      setLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [filtreLog]);

  useEffect(() => { chargerCampagnes(); }, [chargerCampagnes]);
  useEffect(() => {
    if (onglet === 'listes')     chargerListes();
    if (onglet === 'historique') chargerLogs();
  }, [onglet, chargerListes, chargerLogs]);

  // ── Toggle pause/actif ─────────────────────────────────────────────────────

  async function toggleStatut(campagne) {
    try {
      await api.patch(`/emails/campagnes/${campagne.id}/toggle`);
      await chargerCampagnes();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  }

  // ── Ouvrir modal envoi avec preview ───────────────────────────────────────

  async function ouvrirModalEnvoi(campagne) {
    try {
      const preview = await api.get(`/emails/campagnes/${campagne.id}/preview`);
      setModalEnvoi({ campagne, preview });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  }

  // ── Confirmer envoi ────────────────────────────────────────────────────────

  async function confirmerEnvoi(listeOverride, emailsManuels) {
    if (!modalEnvoi) return;
    setLoading(true);
    try {
      const payload = {
        confirme: true,
        liste_override: listeOverride || null,
        emails_manuels: emailsManuels || null,
      };
      const res = await api.post(`/emails/campagnes/${modalEnvoi.campagne.id}/envoyer`, payload);
      setMsg({ type: 'success', text: `${res.envoyes ?? 0} email(s) envoyé(s) avec succès.` });
      setModalEnvoi(null);
      await chargerCampagnes();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }

  // ── Sync WC → Brevo ────────────────────────────────────────────────────────

  async function syncBrevo() {
    setSyncing(true);
    try {
      const res = await api.post('/emails/sync-brevo', {});
      setMsg({ type: 'success', text: `Sync terminée : ${res.syncs} contact(s) synchronisé(s).` });
      if (onglet === 'listes') chargerListes();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSyncing(false);
    }
  }

  // ─── GROUPES DE CAMPAGNES ─────────────────────────────────────────────────

  const automatiques = campagnes.filter(c => c.declencheur === 'automatique');
  const manuelles    = campagnes.filter(c => c.declencheur !== 'automatique');

  if (loading && campagnes.length === 0 && onglet === 'campagnes') return <EmailSkeleton />;

  // ─── RENDER ───────────────────────────────────────────────────────────────

  return (
    <div className="email-central">
      {/* En-tête */}
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-title">Centre de contrôle emails</h1>
          <p className="page-subtitle muted">Campagnes Brevo, listes de contacts et historique des envois</p>
        </div>
        <button className="btn btn-ghost" onClick={syncBrevo} disabled={syncing}>
          <Icon name="rafraichir" size={15} />
          {syncing ? 'Synchronisation…' : 'Sync WooCommerce'}
        </button>
      </div>

      {/* Message flash */}
      {msg && (
        <div
          className={msg.type === 'error' ? 'error-inline' : 'email-msg-success'}
          onClick={() => setMsg(null)}
          style={{ marginBottom: 'var(--space-4)', cursor: 'pointer' }}
        >
          <Icon name={msg.type === 'error' ? 'alerte' : 'succes'} size={15} />
          {msg.text}
        </div>
      )}

      {/* Onglets */}
      <div className="email-tabs">
        {[
          { id: 'campagnes',  label: 'Campagnes' },
          { id: 'listes',     label: 'Listes' },
          { id: 'historique', label: 'Historique' },
        ].map(t => (
          <button
            key={t.id}
            className={`email-tab${onglet === t.id ? ' active' : ''}`}
            onClick={() => setOnglet(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── ONGLET CAMPAGNES ──────────────────────────────────────────────────── */}
      {onglet === 'campagnes' && (
        <div className="email-campagnes">

          {/* Emails automatiques */}
          <div className="campagnes-section">
            <div className="card-header" style={{ marginBottom: 'var(--space-4)' }}>
              <div className="card-header-left">
                <span className="card-header-icon"><Icon name="auto" size={14} /></span>
                <span className="card-title">Emails automatiques</span>
              </div>
            </div>
            <div className="campagnes-grid">
              {automatiques.map(c => (
                <CarteCampagne
                  key={c.id}
                  campagne={c}
                  onToggle={() => toggleStatut(c)}
                  onEnvoyer={() => ouvrirModalEnvoi(c)}
                />
              ))}
              {automatiques.length === 0 && (
                <div className="empty-state" style={{ padding: 'var(--space-6)' }}>
                  <Icon name="email" size={28} />
                  <p className="empty-state__titre">Aucune campagne automatique</p>
                </div>
              )}
            </div>
          </div>

          {/* Emails manuels */}
          <div className="campagnes-section">
            <div className="card-header" style={{ marginBottom: 'var(--space-4)' }}>
              <div className="card-header-left">
                <span className="card-header-icon"><Icon name="modifier" size={14} /></span>
                <span className="card-title">
                  Emails manuels
                  <span className="muted" style={{ fontWeight: 400, fontSize: 12, marginLeft: 8 }}>(confirmation requise)</span>
                </span>
              </div>
            </div>
            <div className="campagnes-grid">
              {manuelles.map(c => (
                <CarteCampagne
                  key={c.id}
                  campagne={c}
                  onToggle={() => toggleStatut(c)}
                  onEnvoyer={() => ouvrirModalEnvoi(c)}
                />
              ))}
              {manuelles.length === 0 && (
                <div className="empty-state" style={{ padding: 'var(--space-6)' }}>
                  <Icon name="email" size={28} />
                  <p className="empty-state__titre">Aucune campagne manuelle</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── ONGLET LISTES ─────────────────────────────────────────────────────── */}
      {onglet === 'listes' && (
        <div className="email-listes">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
            <button className="btn btn-ghost" onClick={chargerListes} disabled={loading}>
              <Icon name="rafraichir" size={15} />
              {loading ? 'Chargement…' : 'Actualiser'}
            </button>
          </div>

          {loading ? (
            <div className="listes-grid">
              {[0,1,2,3].map(i => (
                <div key={i} className="card skeleton" style={{ minHeight: 100 }} />
              ))}
            </div>
          ) : listes.length === 0 ? (
            <div className="empty-state">
              <Icon name="liste" size={32} />
              <p className="empty-state__titre">Aucune liste trouvée</p>
            </div>
          ) : (
            <div className="listes-grid">
              {listes.map(l => (
                <div key={l.cle} className="carte-liste card rise-in">
                  <div className="liste-header">
                    <span className="liste-icone-icon">
                      <Icon name={ICONE_LISTE_MAP[l.cle] || 'email'} size={22} />
                    </span>
                    <div style={{ flex: 1 }}>
                      <div className="liste-nom">{l.nom}</div>
                      {l.description && (
                        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{l.description}</div>
                      )}
                    </div>
                    <div className="liste-count">{l.nb_contacts.toLocaleString('fr-CA')}</div>
                  </div>
                  <div className="liste-footer">
                    {l.list_id ? (
                      <span className="muted" style={{ fontSize: 11 }}>ID Brevo : {l.list_id}</span>
                    ) : (
                      <span className="danger" style={{ fontSize: 11 }}>
                        <Icon name="alerte" size={12} />
                        Variable .env non configurée
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── ONGLET HISTORIQUE ─────────────────────────────────────────────────── */}
      {onglet === 'historique' && (
        <div className="email-historique">
          <div className="historique-filtres">
            <div className="field">
              <label>Du</label>
              <input
                type="date"
                value={filtreLog.date_debut}
                onChange={e => setFiltreLog(f => ({ ...f, date_debut: e.target.value }))}
              />
            </div>
            <div className="field">
              <label>Au</label>
              <input
                type="date"
                value={filtreLog.date_fin}
                onChange={e => setFiltreLog(f => ({ ...f, date_fin: e.target.value }))}
              />
            </div>
            <button className="btn btn-ghost" onClick={chargerLogs} style={{ alignSelf: 'flex-end' }}>
              <Icon name="recherche" size={15} />
              Filtrer
            </button>
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {[0,1,2,3,4].map(i => (
                <div key={i} className="skeleton" style={{ height: 44 }} />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="empty-state">
              <Icon name="liste" size={32} />
              <p className="empty-state__titre">Aucun envoi enregistré pour cette période</p>
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="esc-table">
                <thead>
                  <tr>
                    <th>Date / Heure</th>
                    <th>Type</th>
                    <th>Destinataire</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(l => (
                    <tr key={l.id}>
                      <td className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {formatDate(l.envoye_le)}
                      </td>
                      <td>{l.campagne_nom || l.type_email}</td>
                      <td style={{ fontSize: 13 }}>{l.destinataire}</td>
                      <td>
                        {l.statut === 'envoye' ? (
                          <span className="badge badge--success">
                            <Icon name="check" size={11} /> Envoyé
                          </span>
                        ) : l.statut === 'echec' ? (
                          <span className="badge badge--danger">
                            <Icon name="erreur" size={11} /> Échec
                          </span>
                        ) : (
                          <span className="badge">{l.statut}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── MODAL ENVOI ───────────────────────────────────────────────────────── */}
      {modalEnvoi && (
        <ModalEnvoi
          campagne={modalEnvoi.campagne}
          preview={modalEnvoi.preview}
          onConfirmer={(listeOverride, emailsManuels) => confirmerEnvoi(listeOverride, emailsManuels)}
          onFermer={() => setModalEnvoi(null)}
          loading={loading}
        />
      )}
    </div>
  );
}

// ─── CARTE CAMPAGNE ───────────────────────────────────────────────────────────

function CarteCampagne({ campagne: c, onToggle, onEnvoyer }) {
  const badgeType  = BADGE_TYPE[c.type]              || { label: c.type,        cls: '' };
  const badgeDeclH = BADGE_DECLENCHEUR[c.declencheur] || { label: c.declencheur, cls: '' };
  const estPause   = c.statut === 'pause';
  const estManuel  = c.declencheur !== 'automatique';

  return (
    <div className={`carte-campagne card rise-in${estPause ? ' campagne-pause' : ''}`}>
      <div className="campagne-header">
        <span
          className="campagne-statut-dot"
          style={{ background: estPause ? 'var(--muted)' : 'var(--success)' }}
          title={estPause ? 'En pause' : 'Actif'}
        />
        <span className="campagne-nom">{c.nom}</span>
        <span className={`badge ${badgeType.cls}`}>{badgeType.label}</span>
      </div>

      <div className="campagne-meta">
        <span className={`badge ${badgeDeclH.cls}`}>{badgeDeclH.label}</span>
        {c.template_brevo_id && (
          <span className="muted" style={{ fontSize: 11 }}>Template #{c.template_brevo_id}</span>
        )}
        {c.liste_cible && (
          <span className="muted" style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Icon name={ICONE_LISTE_MAP[c.liste_cible] || 'email'} size={12} />
            {LABELS_LISTE[c.liste_cible] || c.liste_cible}
          </span>
        )}
      </div>

      <div className="campagne-stats">
        <span className="muted" style={{ fontSize: 12 }}>
          {c.nb_envoyes > 0 ? `${c.nb_envoyes} envoyé(s)` : 'Aucun envoi'}
        </span>
        {c.dernier_envoi && (
          <span className="muted" style={{ fontSize: 11 }}>
            · Dernier : {formatDateCourte(c.dernier_envoi)}
          </span>
        )}
      </div>

      {c.notes && (
        <p className="campagne-notes muted">{c.notes}</p>
      )}

      <div className="campagne-actions">
        <button
          className="btn btn-ghost btn--sm"
          onClick={onToggle}
          title={estPause ? 'Réactiver' : 'Mettre en pause'}
        >
          <Icon name={estPause ? 'demarrer' : 'arreter'} size={14} />
          {estPause ? 'Activer' : 'Pause'}
        </button>
        {estManuel && !estPause && (
          <button className="btn btn-primary btn--sm" onClick={onEnvoyer}>
            <Icon name="email" size={14} />
            Envoyer
          </button>
        )}
        {estManuel && estPause && (
          <button className="btn btn-ghost btn--sm" disabled>En pause</button>
        )}
      </div>
    </div>
  );
}

// ─── MODAL ENVOI ─────────────────────────────────────────────────────────────

function ModalEnvoi({ campagne, preview, onConfirmer, onFermer, loading }) {
  const [listeCible,    setListeCible]    = useState('defaut');
  const [emailsManuels, setEmailsManuels] = useState('');
  const [nbContacts,    setNbContacts]    = useState({});

  useEffect(() => {
    api.get('/emails/listes').then(data => {
      if (Array.isArray(data)) {
        const map = {};
        data.forEach(l => { map[l.cle] = l.nb_contacts; });
        setNbContacts(map);
      }
    }).catch(() => {});
  }, []);

  function getNbDestinataires() {
    if (listeCible === 'manuel') {
      return emailsManuels.split(',').map(e => e.trim()).filter(e => e.includes('@')).length;
    }
    if (listeCible === 'defaut') return preview.nb_destinataires;
    return nbContacts[listeCible] || 0;
  }

  function handleConfirmer() {
    const listeOverride = listeCible === 'defaut' ? null : listeCible;
    const emailsList    = listeCible === 'manuel'
      ? emailsManuels.split(',').map(e => e.trim()).filter(e => e.includes('@'))
      : null;
    onConfirmer(listeOverride, emailsList);
  }

  const nb = getNbDestinataires();

  return (
    <div className="modal-overlay" onClick={onFermer}>
      <div className="modal-box modal-box-liste" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: 15, fontFamily: 'Cinzel, serif', fontWeight: 600 }}>
            Envoyer — {campagne.nom}
          </h2>
          <button className="btn btn-ghost btn--sm" onClick={onFermer}>
            <Icon name="fermer" size={15} />
          </button>
        </div>

        {/* Aperçu template */}
        <div className="modal-section">
          <div className="modal-label">Aperçu</div>
          <table className="modal-preview-table">
            <tbody>
              {preview.template_brevo_id && (
                <tr>
                  <td className="muted">Template</td>
                  <td>#{preview.template_brevo_id}</td>
                </tr>
              )}
              <tr>
                <td className="muted">Expéditeur</td>
                <td>{preview.expediteur}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Sélecteur de liste */}
        <div className="modal-section">
          <div className="modal-label">Envoyer à</div>
          <div className="liste-options">
            <label className={`liste-option${listeCible === 'defaut' ? ' selected' : ''}`}>
              <input type="radio" value="defaut" checked={listeCible === 'defaut'}
                onChange={() => setListeCible('defaut')} />
              <span className="liste-option-nom">
                <Icon name={ICONE_LISTE_MAP[campagne.liste_cible] || 'email'} size={14} />
                {LABELS_LISTE[campagne.liste_cible] || campagne.liste_cible}
                <span className="liste-option-defaut-badge">défaut</span>
              </span>
              <span className="liste-option-nb">{preview.nb_destinataires.toLocaleString('fr-CA')} contacts</span>
            </label>

            {OPTIONS_LISTE.filter(o => o.cle !== campagne.liste_cible).map(opt => (
              <label key={opt.cle} className={`liste-option${listeCible === opt.cle ? ' selected' : ''}`}>
                <input type="radio" value={opt.cle} checked={listeCible === opt.cle}
                  onChange={() => setListeCible(opt.cle)} />
                <span className="liste-option-nom">
                  <Icon name={ICONE_LISTE_MAP[opt.cle] || 'email'} size={14} />
                  {opt.label}
                </span>
                {opt.cle !== 'manuel' && (
                  <span className="liste-option-nb">
                    {(nbContacts[opt.cle] || 0).toLocaleString('fr-CA')} contacts
                  </span>
                )}
              </label>
            ))}

            {listeCible === 'manuel' && (
              <textarea
                className="emails-manuels-input"
                placeholder="jean@example.com, marie@example.com..."
                value={emailsManuels}
                onChange={e => setEmailsManuels(e.target.value)}
                rows={3}
              />
            )}
          </div>

          <div className="liste-resume">
            <span className="liste-resume-nb">{nb.toLocaleString('fr-CA')} personne{nb !== 1 ? 's' : ''}</span>
            {' '}recevront cet email.
          </div>
        </div>

        {campagne.notes && (
          <div className="modal-section">
            <div className="modal-label">Notes</div>
            <p className="muted" style={{ fontSize: 13 }}>{campagne.notes}</p>
          </div>
        )}

        <div className="modal-warning">
          <Icon name="attention" size={14} />
          Cet envoi ne peut pas être annulé.
        </div>

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onFermer}>Annuler</button>
          <button className="btn btn-primary" onClick={handleConfirmer}
            disabled={loading || (listeCible === 'manuel' && nb === 0)}>
            {loading ? 'Envoi en cours…' : 'Confirmer et envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
}
